import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import has from 'lodash.has';
import { decamelize } from 'humps';
import { useSelector } from 'react-redux';

import { Div, Icon, Tag, Text } from '@pinpoint/design-kit';

import Rbac from 'src/containers/rbac';
import { formatNumber, formatPercent, getModelTagStyles } from 'src/functions';
import { ROUTES } from 'src/config';
import DeprecatedTag from 'src/components/deprecated_tag';
import CONSTANTS from 'src/store/constants';
import ActuarialDownloads from './actuarial_downloads';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);

/* eslint-disable react/prop-types */
export function CellActions({
  id,
  recentStatus,
  siteRole,
  hiddenByRole,
  setModelToApproveId,
  setModelToDeleteId,
  setModelToHideId,
  setModelToCopyId,
  setHideModalOpen,
  setCopyModalOpen,
  setApproveModalOpen,
  setDeleteModalOpen,
  checkDeleteModel,
  enableApproveModel = true,
}) {
  const copyEligibleRoles = [
    CONSTANTS.ROLE.SUPER_ADMIN,
    CONSTANTS.ROLE.ACTUARY,
    CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
    CONSTANTS.ROLE.CLIENT_SERVICES,
  ];
  return (
    <>
      {enableApproveModel && recentStatus === CONSTANTS.TAL_MODEL.STATE.PENDING_APPROVAL ? (
        <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
          <Icon
            name="Checked"
            cursor="pointer"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            size=".75rem"
            m={{ r: '0.3125rem' }}
            data-test="approve"
            data-tip="Approve model"
            onClick={() => {
              setModelToApproveId(id);
              setApproveModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={CONSTANTS.ROLES.slice(0, 3)}
        fallback={
          siteRole === CONSTANTS.ROLE.CLIENT_SERVICES ? (
            <Icon
              name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
              viewBox={hiddenByRole ? '3 33 66 29' : undefined}
              data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
              color="lightGrey"
              size=".75rem"
              m={{ r: '0.3125rem' }}
              data-test={hiddenByRole ? 'show' : 'hide'}
            />
          ) : null
        }
      >
        <Icon
          name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
          viewBox={hiddenByRole ? '3 33 66 29' : undefined}
          data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
          cursor="pointer"
          hoverTransform="scale(1.25)"
          color="lightGrey"
          hoverColor="brand500"
          size=".75rem"
          m={{ r: '0.3125rem' }}
          data-test={hiddenByRole ? 'show' : 'hide'}
          onClick={() => {
            setModelToHideId(id);
            setHideModalOpen(true);
          }}
        />
      </Rbac>
      {CONSTANTS.TAL_MODEL.STATE_GROUPINGS[recentStatus] === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED ? (
        <Rbac roles={copyEligibleRoles} fallback={null}>
          <Icon
            name="Copy"
            size=".75rem"
            viewBox="0 0 14 14"
            data-test={`copy-model-${id}`}
            cursor="pointer"
            data-tip="Copy this model to a different client"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            m={{ r: '0.3125rem' }}
            onClick={() => {
              setModelToCopyId(id);
              setCopyModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
        fallback={<Icon color="tabRest" name="Delete" cursor="not-allowed" size=".75rem" m={{ x: '0' }} />}
      >
        <Icon
          name="Delete"
          cursor="pointer"
          hoverTransform="scale(1.25)"
          size=".75rem"
          color="lightGrey"
          hoverColor="danger800"
          m={{ x: '0' }}
          data-test="delete"
          data-tip="Delete model"
          onClick={() => {
            setModelToDeleteId(id);
            checkDeleteModel(id);
            setDeleteModalOpen(true);
          }}
        />
      </Rbac>
    </>
  );
}

export function ActuarialCellActions({
  id,
  modelName,
  dataModelType,
  recentStatus,
  childrenRecentStatusList = [],
  hiddenByRole,
  metadata,
  data,
  validation,
  role,
  siteRole,
  originalTenant,
  setModelToApproveId,
  setModelToDeleteId,
  setModelToDeleteType,
  setModelToHideId,
  setModelToCopyId,
  setHideModalOpen,
  setCopyModalOpen,
  setDeleteModalOpen,
  checkDeleteModel,
  setApproveModalOpen,
  fetchEntityDl,
  fetchModelDl,
  enableApproveModel = true,
}) {
  const copyEligibleRoles = [
    CONSTANTS.ROLE.SUPER_ADMIN,
    CONSTANTS.ROLE.ACTUARY,
    CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
    CONSTANTS.ROLE.CLIENT_SERVICES,
  ];
  return (
    <>
      {enableApproveModel &&
      recentStatus === CONSTANTS.TAL_MODEL.STATE.PENDING_APPROVAL &&
      dataModelType !== CONSTANTS.TAL_MODEL.TAG.ACTUARIAL ? (
        <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
          <Icon
            name="Checked"
            cursor="pointer"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            size=".75rem"
            m={{ r: '0.3125rem' }}
            data-test="approve"
            data-tip="Approve model"
            onClick={() => {
              setModelToApproveId(id);
              setApproveModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={CONSTANTS.ROLES.slice(0, 3)}
        fallback={
          siteRole === CONSTANTS.ROLE.CLIENT_SERVICES ? (
            <Icon
              name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
              viewBox={hiddenByRole ? '3 33 66 29' : undefined}
              data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
              color="lightGrey"
              size=".75rem"
              m={{ r: '0.3125rem' }}
              data-test={hiddenByRole ? 'show' : 'hide'}
            />
          ) : null
        }
      >
        <Icon
          name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
          cursor="pointer"
          viewBox={hiddenByRole ? '3 33 66 29' : undefined}
          data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
          hoverTransform="scale(1.25)"
          color="lightGrey"
          hoverColor="brand500"
          size=".75rem"
          m={{ r: '0.3125rem' }}
          data-test={hiddenByRole ? 'show' : 'hide'}
          onClick={() => {
            setModelToHideId(id);
            setHideModalOpen(true);
          }}
        />
      </Rbac>
      {CONSTANTS.TAL_MODEL.TAG.ACTUARIAL === dataModelType &&
      CONSTANTS.TAL_MODEL.STATE_GROUPINGS[recentStatus] === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED &&
      childrenRecentStatusList.every(
        (status) => CONSTANTS.TAL_MODEL.STATE_GROUPINGS[status] === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED
      ) ? (
        <Rbac roles={copyEligibleRoles} fallback={null}>
          <Icon
            name="Copy"
            size=".75rem"
            viewBox="0 0 14 14"
            data-test={`copy-model-${id}`}
            cursor="pointer"
            data-tip="Copy this model to a different client"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            m={{ r: '0.3125rem' }}
            onClick={() => {
              setModelToCopyId(id);
              setCopyModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
        fallback={<Icon color="tabRest" name="Delete" cursor="not-allowed" size=".75rem" m={{ r: '0.3125rem' }} />}
      >
        <Icon
          name="Delete"
          cursor="pointer"
          hoverTransform="scale(1.25)"
          size=".75rem"
          color="lightGrey"
          hoverColor="danger800"
          m={{ r: '0.3125rem' }}
          data-test="delete"
          data-tip="Delete model"
          onClick={() => {
            setModelToDeleteId(id);
            setModelToDeleteType(dataModelType);
            checkDeleteModel(id);
            setDeleteModalOpen(true);
          }}
        />
      </Rbac>
      {CONSTANTS.TAL_MODEL.STATE_GROUPINGS[recentStatus] === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED ? (
        <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
          <ActuarialDownloads
            id={id}
            modelMetadata={metadata}
            modelData={data}
            validationCluster={validation}
            dataModelType={dataModelType}
            iconSize=".75rem"
            originalTenant={originalTenant}
            role={role}
            fetchEntityDl={fetchEntityDl}
            fetchModelDl={fetchModelDl}
          />
        </Rbac>
      ) : null}
    </>
  );
}

export function CellDate({ value, ...props }) {
  return (
    <Text
      textWeight="400"
      textSize="small"
      data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}
      {...props}
    >
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

export function CellStatus(props) {
  const { isRemediated } = props.value;
  if (isRemediated) return CellStatusRemediated(props);
  return CellStatusUnremediated(props);
}

export function CellStatusRemediated({ value: { value, fairnessMetadata = {} } }) {
  let customStatus = 'Completed';
  const { treatment_type: treatmentType, lower_bound: lowerBound, upper_bound: upperBound } = fairnessMetadata;
  const role = useSelector((state) => state.app?.profile?.role);
  const siteRole = useSelector((state) => state.app?.profile?.siteRole);
  customStatus += [role, siteRole].some((r) => CONSTANTS.ROLES.slice(0, 3).includes(r))
    ? `<br />Remediated so the AIRs for ${
        CONSTANTS.TAL_MODEL.FAIRNESS_CLASS[treatmentType?.toUpperCase?.()]
      } remain between ${lowerBound} and ${upperBound}`
    : '';
  switch (value) {
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.PENDING_APPROVAL:
      return <Div rounded="sm" bg="marigold" h=".9rem" w=".9rem" data-test="cell-status" data-tip="Pending approval" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.IN_PROGRESS:
      return <Div rounded="sm" bg="blueLight" h=".9rem" w=".9rem" data-test="cell-status" data-tip="In progress" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.ERRORED:
      return <Div rounded="sm" bg="vividRed" h=".9rem" w=".9rem" data-test="cell-status" data-tip="Errored" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED:
      return <Div rounded="sm" bg="rioGrande" h=".9rem" w=".9rem" data-test="cell-status" data-tip={customStatus} />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.WAITING:
      return (
        <Div
          rounded="sm"
          bg="talUnknown"
          h=".9rem"
          w=".9rem"
          data-tip="Pending completion of frequency and severity models"
        />
      );
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.UNKNOWN:
    default:
      return <Div rounded="sm" bg="talUnknown" h=".9rem" w=".9rem" />;
  }
}

export function CellStatusUnremediated({ value: { stats = [], value, trainingType } }) {
  const metricFields = {
    transformer: ['validLoss', 'validMse', 'validAuc'],
    catboost: ['nllTest', 'devianceTest', 'aucTest', 'logLossTest'],
  };

  let prefix = `Completed<br />Model Type: ${trainingType === 'catboost' ? 'CatBoost' : 'Deep Learning'}<br />`;
  const calculatedStats = stats?.reduce?.((acc, cur, idx) => {
    // if (!has(cur, 'foldId')) return acc;
    if (has(cur, 'runTime')) {
      acc.runTime = acc.runTime === undefined ? cur.runTime : cur.runTime + acc.runTime;
      if (idx === stats.length - 1) acc.runTime /= stats.length;
    }

    [...metricFields.transformer, ...metricFields.catboost].forEach((field) => {
      if (has(cur, field)) {
        acc[field] = acc[field] === undefined ? cur[field] : cur[field] + acc[field];
        if (idx === stats.length - 1) acc[field] /= stats.length;
      }
    });

    return acc;
  }, {});
  let customStatus = stats
    ?.map?.(({ foldId, ...items }, idx) => {
      let out = '';
      if (has(items, 'flowId')) prefix += `Flow ID: ${items.flowId}<br />`;
      if (foldId) out = `${out}Fold ${foldId}: `;
      else out = `${out}Fold ${idx + 1}: `;
      [...metricFields.transformer, ...metricFields.catboost].forEach((key) => {
        if (has(items, key)) out = `${out}${decamelize(key)}: ${+items[key].toFixed(3)}; `;
      });
      return out.trim().replace(/(^;)|(;$)/g, '');
    })
    ?.join('<br />');

  let avgLine = 'Average: ';
  [...metricFields.transformer, ...metricFields.catboost, 'runTime'].forEach((key) => {
    if (has(calculatedStats, key) && key !== 'runTime')
      avgLine = `${avgLine}${decamelize(key)}: ${+calculatedStats[key].toFixed(3)}; `;
    else if (has(calculatedStats, key) && key === 'runTime') {
      avgLine = `${avgLine}${decamelize(key)}: ${dayjs.duration(+calculatedStats[key]).format('HH:mm')}; `;
    }
  });
  avgLine = avgLine.trim().replace(/(^;)|(;$)/g, '');
  customStatus = customStatus ? `${prefix}${customStatus}<br />${avgLine !== 'Average:' ? avgLine : ''}` : 'Completed';
  switch (value) {
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.PENDING_APPROVAL:
      return <Div rounded="sm" bg="marigold" h=".9rem" w=".9rem" data-test="cell-status" data-tip="Pending approval" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.IN_PROGRESS:
      return <Div rounded="sm" bg="blueLight" h=".9rem" w=".9rem" data-test="cell-status" data-tip="In progress" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.ERRORED:
      return <Div rounded="sm" bg="vividRed" h=".9rem" w=".9rem" data-test="cell-status" data-tip="Errored" />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED:
      return <Div rounded="sm" bg="rioGrande" h=".9rem" w=".9rem" data-test="cell-status" data-tip={customStatus} />;
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.WAITING:
      return (
        <Div
          rounded="sm"
          bg="talUnknown"
          h=".9rem"
          w=".9rem"
          data-tip="Pending completion of frequency and severity models"
        />
      );
    case CONSTANTS.TAL_MODEL.STATE_GROUPS.UNKNOWN:
    default:
      return <Div rounded="sm" bg="talUnknown" h=".9rem" w=".9rem" />;
  }
}

export function CellPercent({ value, ...props }) {
  return (
    <Text
      textWeight="400"
      textSize="small"
      {...props}
      data-tip={Number.isFinite(value) ? formatPercent(value) : undefined}
    >
      {Number.isFinite(value) ? formatPercent(value) : value}
    </Text>
  );
}

export function CellText({ value, ...props }) {
  return (
    <Text textWeight="400" textSize="small" {...props}>
      {value}
    </Text>
  );
}

export function CellName({ value, row, getDeprecatedTag, ...props }) {
  const { dataPayload } = row?.original?.modelData ?? {};
  const segment = dataPayload?.negativeSegment ?? dataPayload?.positiveSegment ?? dataPayload?.actuarialSegment;
  const { platform } = segment?.refresh ?? {};
  return (
    <>
      <DeprecatedTag tag={getDeprecatedTag(platform)} m={{ r: '0.5rem' }} />
      <Text textWeight="400" textSize="small" {...props}>
        {value}
      </Text>
    </>
  );
}

export function CellNumber({ value, ...props }) {
  return (
    <Text textWeight="400" textSize="small" {...props} data-tip={formatNumber(value)}>
      {formatNumber(value)}
    </Text>
  );
}

export function CellHit({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.positiveSegment?.filter && dataPayload?.positiveSegment?.dataset) {
    const { positiveSegment } = dataPayload;
    const { filter, dataset, name } = positiveSegment;
    return (
      <Text
        m={{ r: '0.25rem' }}
        data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        maxW="8rem"
        textWeight="400"
        textSize="small"
        align="center"
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
      </Text>
    );
  }
  return null;
}

export function CellHitMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.positiveSegment?.id && dataPayload?.positiveSegment?.matched) {
    const { positiveSegment } = dataPayload;
    const { id, matched } = positiveSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellMiss({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.negativeSegment?.filter && dataPayload?.negativeSegment?.dataset) {
    const { negativeSegment } = dataPayload;
    const { filter, dataset, name } = negativeSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%" m={{ l: '2rem' }}>
        <Text
          m={{ r: '0.25rem' }}
          data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
          maxW="8rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        </Text>
      </Div>
    );
  }
  return null;
}

export function CellMissMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.negativeSegment?.id && dataPayload?.negativeSegment?.matched) {
    const { negativeSegment } = dataPayload;
    const { id, matched } = negativeSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellValidation({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.validationSegment?.filter && dataPayload?.validationSegment?.dataset) {
    const { validationSegment } = dataPayload;
    const { filter, dataset, name } = validationSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%" m={{ l: '2rem' }}>
        <Text
          m={{ r: '0.25rem' }}
          data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
          maxW="8rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        </Text>
      </Div>
    );
  }
  return null;
}

export function CellValidationMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.validationSegment?.id && dataPayload?.validationSegment?.matched) {
    const { validationSegment } = dataPayload;
    const { id, matched } = validationSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellRegulatoryTag({ value, textSize = 'small', iconProps = {}, ...props }) {
  const frameworkName = value?.framework?.name ?? 'F1';
  const frameworkTags = value?.framework?.regulationTags ?? [];
  if (!frameworkTags?.length) return null;
  // NOTE: possible values are "<STATE>-<LINEOFBUSINESS>"
  const dataTipMap = new Map();
  frameworkTags.forEach((tag) => {
    const [stateCode, lob] = tag.split('-');
    const lobName = CONSTANTS.TAL_MODEL.FRAMEWORK_LOB_NAME?.[lob] ?? 'Other';
    if (dataTipMap.has(lobName)) dataTipMap.set(lobName, `${dataTipMap.get(lobName)}, ${stateCode}`);
    else dataTipMap.set(lobName, stateCode);
  });
  let dataTip = '';
  dataTipMap.forEach((states, lob) => {
    dataTip += `${lob}<br/>`;
    dataTip += `${states}<br /><br />`;
  });
  return (
    <Tag
      // @ts-ignore
      p="0px"
      m={{ r: '.25rem' }}
      // @ts-ignore
      h="1.125rem"
      textColor="grey700"
      borderColor="grey700"
      border="1px solid"
      data-test="framework-tag"
      data-tip={dataTip}
      suffix={
        <Div h="100%" d="flex" align="center" bg="grey700">
          {/* @ts-ignore */}
          <Icon
            name="Regulatory"
            size={iconProps?.size ?? '.75rem'}
            viewBox={iconProps?.viewBox ?? '0 0 38 38'}
            color="white"
            m={{ x: '.25rem' }}
          />
        </Div>
      }
      {...props}
    >
      <Text textSize={textSize} m={{ x: '.25rem' }}>
        {frameworkName}
      </Text>
    </Tag>
  );
}

export function CellTag({ value, ...props }) {
  let isRemediated = value?.isRemediated;
  const tag = value?.dataModelType;
  const typeDetail = value?.typeDetail;
  const predictionType = value?.predictionType;
  const dataTest = value?.dataTest;
  const isLegacy = value?.isLegacy;
  const modelVersion =
    value?.modelVersion
      ?.split?.('_')
      ?.map?.((item) => +item)
      ?.filter?.((item) => item)?.[0] ?? null;
  let modelGte4 = modelVersion >= 4;
  let extraText = '';
  if (tag === 'CLASSIFIER') {
    if (predictionType === 'Early Cancellation') extraText = `${predictionType}, ${typeDetail}-day window`;
    else if (['Other', 'Premium Leakage'].includes(predictionType)) extraText = `${predictionType}, ${typeDetail}`;
    else extraText = predictionType;
  } else if (tag === 'ACTUARIAL') {
    extraText = typeDetail;
  }
  if (isLegacy) {
    modelGte4 = false;
    isRemediated = false;
  }
  const { tip, color, name, viewBox } = getModelTagStyles(tag, extraText);
  return (
    <Rbac
      roles={CONSTANTS.ROLES.slice(0, 4)}
      fallback={
        <Tag
          p="0px"
          // @ts-ignore
          h="1.125rem"
          textColor={color}
          borderColor={color}
          border="1px solid"
          data-tip={tip}
          prefix={
            <Div h="100%" d="flex" align="center">
              <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ l: '.25rem' }} />
            </Div>
          }
          suffix={
            isRemediated || modelGte4 ? (
              <Div h="100%" d="flex" align="center" bg={color} data-test="tag-remediated">
                <Icon name="Fairness" size=".75rem" viewBox="0 0 37 36" color="white" m={{ x: '.25rem' }} />
              </Div>
            ) : null
          }
          data-test={dataTest}
          {...props}
        >
          <Text textSize="small" m={{ x: '.25rem' }}>
            {CONSTANTS.TAL_MODEL.TAG_NAME[tag] || CONSTANTS.SEGMENT.TAG[tag]}
          </Text>
        </Tag>
      }
    >
      <Tag
        p="0px"
        // @ts-ignore
        h="1.125rem"
        textColor={color}
        borderColor={color}
        border="1px solid"
        data-tip={tip}
        prefix={
          <Div h="100%" d="flex" align="center">
            <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ l: '.25rem' }} />
          </Div>
        }
        suffix={
          isRemediated ? (
            <Div h="100%" d="flex" align="center" bg={color} data-test="tag-remediated">
              <Icon name="Fairness" size=".75rem" viewBox="0 0 37 36" color="white" m={{ x: '.25rem' }} />
            </Div>
          ) : undefined
        }
        data-test={dataTest}
        {...props}
      >
        <Text textSize="small" m={{ x: '.25rem' }}>
          {CONSTANTS.TAL_MODEL.TAG_NAME[tag] || CONSTANTS.SEGMENT.TAG[tag]}
        </Text>
      </Tag>
    </Rbac>
  );
}

export function CellTagTuple({ value, rightMargin, ...props }) {
  const framework = value?.framework;
  const isRemediated = value?.isRemediated;
  const tag = value?.dataModelType;
  const typeDetail = value?.typeDetail;
  const predictionType = value?.predictionType;
  const dataTest = value?.dataTest;
  let extraText = '';
  if (tag === 'CLASSIFIER') {
    if (predictionType === 'Early Cancellation') extraText = `${predictionType}, ${typeDetail}-day window`;
    else if (['Other', 'Premium Leakage'].includes(predictionType)) extraText = `${predictionType}, ${typeDetail}`;
    else extraText = predictionType;
  } else if (tag === 'ACTUARIAL') {
    extraText = typeDetail;
  }
  const { color, name, viewBox, ...rest } = getModelTagStyles(tag, extraText);
  let { tip } = rest;
  if (isRemediated) tip = `(Remediated) ${tip}`;
  return (
    <>
      <Tag
        m={{ r: rightMargin }}
        p="0px"
        // @ts-ignore
        h="1.125rem"
        textColor={color}
        borderColor={color}
        border="1px solid"
        data-tip={tip}
        prefix={
          <Div h="100%" d="flex" align="center">
            <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ l: '.25rem' }} />
          </Div>
        }
        suffix={
          isRemediated ? (
            <Div h="100%" d="flex" align="center" bg={color}>
              <Icon name="Fairness" size=".75rem" viewBox="0 0 37 36" color="white" m={{ x: '.25rem' }} />
            </Div>
          ) : undefined
        }
        data-test={dataTest}
        {...props}
      >
        <Text textSize="small" m={{ x: '.25rem' }}>
          {CONSTANTS.TAL_MODEL.TAG_NAME[tag] || CONSTANTS.SEGMENT.TAG[tag]}
        </Text>
      </Tag>
      {framework
        ? CellRegulatoryTag({
            value: { framework },
            bg: 'white',
            textSize: 'small',
            m: { r: rightMargin },
          })
        : null}
    </>
  );
}
